import { useRef, useState } from "react"
import { makeStyles } from "tss-react/mui"
import SearchIcon from "@mui/icons-material/Search"
import CloseIcon from "@mui/icons-material/Close"
import { Box, FormControl, IconButton, InputAdornment, InputLabel, OutlinedInput, Tooltip } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faMagnifyingGlass, faXmark } from "@fortawesome/pro-light-svg-icons"
import { useDebouncedInput } from "../../hooks/useDebouncedInput"
import { sanitizeSearchQuery } from "../../../lib/stringLib"
import { useAppLocalization } from "../../../modules/localization/components/AppLocalizationProvider"

type StyleProps = Readonly<{
    expandedWidth: string
}>

const useStyles = makeStyles<StyleProps>()((theme, { expandedWidth }) => ({
    container: {
        position: "relative",
        maxWidth: "100%",
    },
    inputRoot: {
        width: 0,
        transition: "width 0.3s ease-out",
        "& input": {
            width: "100%",
            paddingRight: 0,
        },
        "& fieldset": {
            transition: "all 0.3s ease-out",
            opacity: 0,
        },
    },
    borderedInputRoot: {
        width: "2.5rem",
        borderRadius: ".75rem",
        backgroundColor: theme.palette.common.white,
        "& fieldset": {
            opacity: 1,
            borderColor: theme.palette.divider,
        },
    },
    expandedInput: {
        width: expandedWidth,
        "& fieldset": {
            opacity: "100%",
        },
        "& input": {
            paddingRight: "2.75rem",
        },
    },
    iconButton: {
        position: "absolute",
        right: 2,
        zIndex: 1,
    },
    borderedIconButton: {
        top: 0,
        right: 0,
        width: "2.5rem",
        height: "2.5rem",
        borderRadius: ".75rem",
        color: theme.palette.common.black,
    },
}))
type ExpandableSearchProps = Readonly<{
    value: string
    onChange: (value: string) => void
    label: string
    placeholder?: string
    expandedWidth?: string
    bordered?: boolean
}>

export const ExpandableSearch = ({
    value,
    onChange,
    label,
    placeholder = "",
    expandedWidth = "20rem",
    bordered = false,
}: ExpandableSearchProps) => {
    const { classes, cx } = useStyles({ expandedWidth })
    const { l10n } = useAppLocalization()
    const { innerValue, setInnerValue } = useDebouncedInput(value, (newValue) =>
        onChange(sanitizeSearchQuery(newValue)),
    )
    const [expanded, setExpanded] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)
    const hasValue = Boolean(innerValue)
    // collapsing would hide a search that is still filtering
    const isExpanded = expanded || hasValue
    const buttonLabel = l10n.getString(hasValue ? "expandable-search-clear-button" : "expandable-search-search-button")

    const collapse = () => {
        setExpanded(false)
        inputRef.current?.blur()
    }

    const handleButtonClick = () => {
        if (hasValue) {
            setInnerValue("")
            onChange("")
            collapse()
        } else if (isExpanded) {
            collapse()
        } else {
            setExpanded(true)
            inputRef.current?.focus()
        }
    }

    const handleBlur = () => {
        if (!hasValue) setExpanded(false)
    }

    const buttonIcon = bordered ? (
        <FontAwesomeIcon icon={hasValue ? faXmark : faMagnifyingGlass} size="xs" />
    ) : hasValue ? (
        <CloseIcon />
    ) : (
        <SearchIcon />
    )

    return (
        <FormControl className={classes.container} size={bordered ? "small" : undefined}>
            {isExpanded && <InputLabel>{label}</InputLabel>}
            <OutlinedInput
                inputRef={inputRef}
                className={cx(classes.inputRoot, {
                    [classes.borderedInputRoot]: bordered,
                    [classes.expandedInput]: isExpanded,
                })}
                startAdornment={
                    <InputAdornment position="start">
                        <Tooltip title={buttonLabel}>
                            <IconButton
                                onClick={handleButtonClick}
                                // keep focus in the input, otherwise its blur collapses the field before the click
                                onMouseDown={(event) => event.preventDefault()}
                                edge="start"
                                className={cx(classes.iconButton, { [classes.borderedIconButton]: bordered })}
                                aria-label={buttonLabel}
                            >
                                {buttonIcon}
                            </IconButton>
                        </Tooltip>
                    </InputAdornment>
                }
                value={innerValue}
                onChange={(e) => setInnerValue(e.target.value)}
                onFocus={() => setExpanded(true)}
                onBlur={handleBlur}
                label={isExpanded ? label : undefined}
                placeholder={isExpanded ? placeholder : ""}
            />
        </FormControl>
    )
}
